import React, { useState } from "react";
import * as Mui from "@mui/material";
import { useForm } from "react-hook-form";
import { CustomTextBox } from "components/CustomComponents/CustomTextBox";
import { customAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import { themes } from "services/constants";
import { formKeyText, tooltipStyleProps } from "services/constants/Styles";
import { showToast } from "services/utils/Status";
import { Info, DateRange } from "@mui/icons-material";
import moment from "moment";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { CustomButton } from "components/CustomComponents/CustomButton";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { onlyNumberRegex } from "services/constants/patterns";
import { CustomAutoComplete } from "components/CustomComponents";
import { useFetch } from "services/hooks/useFetch";

export const Apply = ({ setSelectedComponent, setAlignment }) => {
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment());
  const [buttonLoader, setButtonLoader] = useState(false);

  const { result: IncentiveGroups } = useFetch(
    `${domain}custom-incentive-groups/`
  );

  const onSubmit = async (data) => {
    setButtonLoader(true);
    const response = await customAPI(
      `${domain}my-incentive-applications/`,
      "POST",
      {
        incentive_group: data?.incentive_id,
        start_date: startDate.format("YYYY-MM-DD"),
        end_date: endDate.format("YYYY-MM-DD"),
        description: data?.reason_for_incentive,
        manual_employee: {
          user: userDetails?.id,
          incentive_amount: parseInt(data?.incentive_amount),
        },
      }
    );
    if (response.ok) {
      showToast("success", "Incentive applied successfully");
      handleClearButtonClick();
      setButtonLoader(false);
    } else {
      setButtonLoader(false);
    }
  };
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      incentive_name: "",
      incentive_amount: "",
      reason_for_incentive: "",
    },
  });

  const handleCallback = async (startDate, endDate) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const label =
    startDate.format("DD/MM/YYYY") + " - " + endDate.format("DD/MM/YYYY");

  const updatedReasonForOvertime = (event) => {
    if (event.target.value.length <= 1024) {
      setValue("reason_for_incentive", event.target.value);
    }
  };

  const handleClearButtonClick = () => {
    setValue("incentive_name", "");
    setValue("incentive_id", "");
    setValue("incentive_amount", "");
    setValue("reason_for_incentive", "");
    setStartDate(moment());
    setEndDate(moment());
    clearErrors(["incentive_name", "incentive_amount", "reason_for_incentive"]);
  };

  const handleAmount = (event) => {
    if (
      event.target.value.length <= 8 &&
      onlyNumberRegex.test(event.target.value)
    ) {
      setValue("incentive_amount", event.target.value);
    }
  };

  const updateIncentiveGroupName = (event, data) => {
    if (data) {
      setValue("incentive_name", data?.label);
      setValue("incentive_id", data?.id);
    } else {
      setValue("incentive_name", null);
      setValue("incentive_id", null);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ padding: 10 }}>
      <Mui.Grid sx={{ paddingLeft: 15, paddingRight: 15, mt: 8 }}>
        <Mui.Grid container>
          <Mui.Grid
            xs={12}
            sx={{
              mt: 5,
              padding: 1,
              backgroundColor: themes.primary,
              borderRadius: "10px 10px 0 0",
              border: `2px solid ${themes.sidebarDivider}`,
              borderBottom: "none",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Mui.Typography
              sx={{ fontSize: 24, fontWeight: 800, font: "bold" }}
            >
              Apply Incentive
            </Mui.Typography>
          </Mui.Grid>
        </Mui.Grid>
        <Mui.Grid
          sx={{
            borderRadius: "0 0 10px 10px",
            border: `2px solid ${themes.sidebarDivider}`,
            padding: 2,
          }}
        >
          <Mui.Grid container>
            <Mui.Grid item xs={5} sx={{ padding: 1 }}>
              <Mui.Typography sx={formKeyText}>
                Incentive Name &nbsp;
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title="Choose the incentive name you want to apply for."
                >
                  <Info />
                </Mui.Tooltip>
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid item xs={0.2}></Mui.Grid>
            <Mui.Grid item xs={5} sx={{ padding: 1, mt: 0.5 }}>
              <CustomAutoComplete
                dataList={IncentiveGroups?.map((obj) => ({
                  ...obj,
                  label: obj.name,
                }))}
                selectedValue={watch("incentive_name")}
                rules={{
                  ...register("incentive_name", {
                    required: "Incentive name is required",
                  }),
                }}
                updateDataFunction={updateIncentiveGroupName}
                placeHolder="Select the incentive name"
                errorMessage={
                  errors?.incentive_name?.message
                    ? errors?.incentive_name?.message
                    : ""
                }
                textBoxError={errors.incentive_name ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid container>
            <Mui.Grid item xs={5} sx={{ padding: 1 }}>
              <Mui.Typography sx={formKeyText}>
                Incentive Type &nbsp;
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title="Displays the type of incentive. In MyZone, this is typically set to Custom and cannot be changed."
                >
                  <Info />
                </Mui.Tooltip>
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid item xs={0.2}></Mui.Grid>
            <Mui.Grid item xs={5} sx={{ padding: 1 }}>
              <Mui.Typography
                sx={{ mt: 1.5, fontSize: 18, fontWeight: "bold" }}
              >
                Custom
              </Mui.Typography>
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid container>
            <Mui.Grid item xs={5} sx={{ padding: 1 }}>
              <Mui.Typography sx={formKeyText}>
                Amount &nbsp;
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title="Input the total incentive amount being claimed for the selected period."
                >
                  <Info />
                </Mui.Tooltip>
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid item xs={0.2}></Mui.Grid>
            <Mui.Grid item xs={5} sx={{ padding: 1 }}>
              <Mui.Typography
                sx={{ mt: 1.5, fontSize: 18, fontWeight: "bold" }}
              >
                <CustomTextBox
                  selectedValue={watch("incentive_amount")}
                  updateDataFunction={handleAmount}
                  rules={{
                    ...register("incentive_amount", {
                      required: "Amount is required",
                    }),
                  }}
                  placeHolder="Enter the amount"
                  errorMessage={
                    errors?.incentive_amount?.message
                      ? errors?.incentive_amount?.message
                      : ""
                  }
                  textBoxError={errors?.incentive_amount ? true : false}
                />
              </Mui.Typography>
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid container>
            <Mui.Grid item xs={5} sx={{ padding: 1 }}>
              <Mui.Typography sx={formKeyText}>
                Date Range &nbsp;
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title="Select the applicable date range during which the incentive was earned."
                >
                  <Info />
                </Mui.Tooltip>
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid item xs={0.2}></Mui.Grid>
            <Mui.Grid item xs={5} sx={{ padding: 1 }}>
              <DateRangePicker
                initialSettings={{
                  startDate: startDate.toDate(),
                  endDate: endDate.toDate(),
                  applyClass: "custom-apply-btn",
                  cancelClass: "custom-cancel-btn",
                  locale: {
                    format: "DD/MM/YYYY",
                  },
                }}
                onCallback={handleCallback}
              >
                <Mui.TextField
                  id="reportrange"
                  variant="outlined"
                  label=""
                  placeholder="Select a date range"
                  value={label}
                  size="small"
                  InputProps={{
                    readOnly: true,
                    notched: false,
                    endAdornment: (
                      <Mui.InputAdornment position="end">
                        <Mui.IconButton>
                          <DateRange />
                        </Mui.IconButton>
                      </Mui.InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true, required: true }}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      legend: {
                        display: "none",
                      },
                    },
                  }}
                />
              </DateRangePicker>
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid container>
            <Mui.Grid item xs={5} sx={{ padding: 1 }}>
              <Mui.Typography sx={formKeyText}>
                Description &nbsp;
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title="Provide a brief reason or note for this incentive claim."
                >
                  <Info />
                </Mui.Tooltip>
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid item xs={0.2}></Mui.Grid>
            <Mui.Grid item xs={5} sx={{ padding: 1 }}>
              <CustomTextBox
                type="multiline"
                Rows={5}
                rules={{
                  ...register("reason_for_incentive", {
                    required: "Description is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else if (value?.trim().length > 255 && value !== "") {
                          return "Maximum length is 255 characters";
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                selectedValue={watch("reason_for_incentive")}
                updateDataFunction={updatedReasonForOvertime}
                placeHolder="Provide a reason for incentive"
                errorMessage={
                  errors?.reason_for_incentive?.message
                    ? errors?.reason_for_incentive?.message
                    : ""
                }
                textBoxError={errors?.reason_for_incentive ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid
            container
            xs={11.7}
            spacing={1}
            sx={{
              paddingTop: 2,
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <>
              {(watch("incentive_name") === "" ||
                watch("incentive_name") === null) &&
              watch("incentive_amount") === "" &&
              watch("reason_for_incentive") === "" ? (
                <CustomButton
                  actionFuntion={() => {
                    setSelectedComponent("Pending");
                    setAlignment("Pending");
                  }}
                  actionName="Cancel"
                  typeName="button"
                />
              ) : (
                <Mui.Grid item>
                  <CustomButton
                    actionFuntion={handleClearButtonClick}
                    actionName="Clear"
                    typeName="button"
                  />
                </Mui.Grid>
              )}
            </>
            <Mui.Grid item>
              <CustomButton
                actionName="Apply"
                typeName="submit"
                disableAction={buttonLoader}
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.Grid>
    </form>
  );
};
